import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Lock, LogOut, Wrench, Receipt, Plus, Check, BookOpen, PhoneCall, Loader2, Upload, LifeBuoy, Trash2, Brain, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  staffLogin, staffMe, setStaffToken, getStaffToken,
  getKnowledge, addKnowledge, ingestKnowledge, getFollowups, addFollowup, toggleFollowup, getBills,
  getCustomerKnowledge, addCustomerKnowledge, deleteCustomerKnowledge, ingestCustomerKnowledge,
} from "@/lib/api";
import { SuggestedFixes, MemoryList } from "./MemoryPanel";
import { JobsPanel } from "./JobsPanel";

function LoginForm({ onLoggedIn }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await staffLogin(username.trim(), password);
      setStaffToken(res.token);
      onLoggedIn(res);
      toast.success(`Signed in as ${res.name}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="p-5 space-y-4" data-testid="staff-login-form">
      <div className="flex items-center gap-2 text-[var(--primary)]">
        <Lock size={16} /> <span className="font-display font-semibold">Staff sign in</span>
      </div>
      <p className="text-sm text-[var(--text-secondary)]">Use your it IN A bit username & password.</p>
      <input
        data-testid="staff-username" value={username} onChange={(e) => setUsername(e.target.value)}
        placeholder="Username" autoComplete="username"
        autoCapitalize="none" autoCorrect="off" spellCheck={false} inputMode="text"
        className="w-full rounded-xl bg-black/30 border border-[var(--border)] px-3 py-2.5 text-sm outline-none focus:border-[var(--primary)] transition-colors"
      />
      <input
        data-testid="staff-password" value={password} onChange={(e) => setPassword(e.target.value)}
        type="password" placeholder="Password" autoComplete="current-password"
        autoCapitalize="none" autoCorrect="off" spellCheck={false}
        className="w-full rounded-xl bg-black/30 border border-[var(--border)] px-3 py-2.5 text-sm outline-none focus:border-[var(--primary)] transition-colors"
      />
      <button
        data-testid="staff-login-submit" disabled={loading || !username || !password}
        className="w-full flex items-center justify-center gap-2 rounded-xl bg-[var(--primary)] text-black font-semibold py-2.5 text-sm disabled:opacity-40 transition-colors hover:bg-[var(--primary-hover)]"
      >
        {loading ? <Loader2 size={16} className="animate-spin" /> : <Lock size={15} />} Sign in
      </button>
    </form>
  );
}

function TechResource({ jobsSearch }) {
  const [kb, setKb] = useState([]);
  const [fups, setFups] = useState([]);
  const [ckb, setCkb] = useState([]);
  const [tab, setTab] = useState("kb");
  const [kbForm, setKbForm] = useState({ title: "", resolution: "" });
  const [fForm, setFForm] = useState({ customer: "", contact: "", note: "" });
  const [ckbForm, setCkbForm] = useState({ title: "", resolution: "", category: "" });
  const [ingesting, setIngesting] = useState(false);
  const [ckbIngesting, setCkbIngesting] = useState(false);
  const fileRef = useRef(null);
  const ckbFileRef = useRef(null);

  const load = useCallback(() => {
    getKnowledge().then(setKb).catch(() => {});
    getFollowups().then(setFups).catch(() => {});
    getCustomerKnowledge().then(setCkb).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (jobsSearch) setTab("jobs"); }, [jobsSearch]);

  const onIngest = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setIngesting(true);
    try {
      const res = await ingestKnowledge(file);
      toast.success(`Added ${res.added} ${res.added === 1 ? "entry" : "entries"} from ${res.source}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't import that document");
    } finally {
      setIngesting(false);
    }
  };

  const onCkbIngest = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setCkbIngesting(true);
    try {
      const res = await ingestCustomerKnowledge(file);
      toast.success(`Added ${res.added} customer ${res.added === 1 ? "guide" : "guides"} from ${res.source}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't import that document");
    } finally {
      setCkbIngesting(false);
    }
  };

  const saveCkb = async () => {
    if (!ckbForm.title.trim() || !ckbForm.resolution.trim()) return;
    await addCustomerKnowledge(ckbForm);
    setCkbForm({ title: "", resolution: "", category: "" });
    toast.success("Customer guide saved");
    load();
  };
  const removeCkb = async (id) => { await deleteCustomerKnowledge(id); toast.success("Guide removed"); load(); };

  const saveKb = async () => {
    if (!kbForm.title.trim() || !kbForm.resolution.trim()) return;
    await addKnowledge(kbForm.title, kbForm.resolution);
    setKbForm({ title: "", resolution: "" });
    toast.success("Fix saved to knowledge base");
    load();
  };
  const saveF = async () => {
    if (!fForm.note.trim()) return;
    await addFollowup(fForm);
    setFForm({ customer: "", contact: "", note: "" });
    toast.success("Follow-up added");
    load();
  };
  const toggle = async (id) => { await toggleFollowup(id); load(); };

  return (
    <div className="flex flex-col h-full" data-testid="tech-resource">
      <div className="flex gap-1 p-2 border-b border-[var(--border)]">
        <button data-testid="tech-tab-jobs" onClick={() => setTab("jobs")}
          className={`flex-1 min-w-0 flex items-center justify-center gap-1 sm:gap-1.5 rounded-lg px-1 py-2 text-[11px] sm:text-xs font-semibold whitespace-nowrap transition-colors ${tab === "jobs" ? "bg-[var(--tech-accent)] text-black" : "text-[var(--text-secondary)] hover:bg-white/5"}`}>
          <ClipboardList size={14} className="hidden sm:block shrink-0" /> Jobs
        </button>
        <button data-testid="tech-tab-kb" onClick={() => setTab("kb")}
          className={`flex-1 min-w-0 flex items-center justify-center gap-1 sm:gap-1.5 rounded-lg px-1 py-2 text-[11px] sm:text-xs font-semibold whitespace-nowrap transition-colors ${tab === "kb" ? "bg-[var(--tech-accent)] text-black" : "text-[var(--text-secondary)] hover:bg-white/5"}`}>
          <BookOpen size={14} className="hidden sm:block shrink-0" /> Tech KB
        </button>
        <button data-testid="tech-tab-ckb" onClick={() => setTab("ckb")}
          className={`flex-1 min-w-0 flex items-center justify-center gap-1 sm:gap-1.5 rounded-lg px-1 py-2 text-[11px] sm:text-xs font-semibold whitespace-nowrap transition-colors ${tab === "ckb" ? "bg-[var(--tech-accent)] text-black" : "text-[var(--text-secondary)] hover:bg-white/5"}`}>
          <LifeBuoy size={14} className="hidden sm:block shrink-0" /> <span className="hidden sm:inline">Customer KB</span><span className="sm:hidden">Cust KB</span>
        </button>
        <button data-testid="tech-tab-followups" onClick={() => setTab("fups")}
          className={`flex-1 min-w-0 flex items-center justify-center gap-1 sm:gap-1.5 rounded-lg px-1 py-2 text-[11px] sm:text-xs font-semibold whitespace-nowrap transition-colors ${tab === "fups" ? "bg-[var(--tech-accent)] text-black" : "text-[var(--text-secondary)] hover:bg-white/5"}`}>
          <PhoneCall size={14} className="hidden sm:block shrink-0" /> Follow-ups
        </button>
        <button data-testid="tech-tab-memory" onClick={() => setTab("mem")}
          className={`flex-1 min-w-0 flex items-center justify-center gap-1 sm:gap-1.5 rounded-lg px-1 py-2 text-[11px] sm:text-xs font-semibold whitespace-nowrap transition-colors ${tab === "mem" ? "bg-[var(--tech-accent)] text-black" : "text-[var(--text-secondary)] hover:bg-white/5"}`}>
          <Brain size={14} className="hidden sm:block shrink-0" /> Memory
        </button>
      </div>
      <ScrollArea className="flex-1">
        {tab === "jobs" ? (
          <JobsPanel search={jobsSearch} />
        ) : tab === "mem" ? (
          <MemoryList />
        ) : tab === "kb" ? (
          <div className="p-4 space-y-3">
            <SuggestedFixes onApproved={load} />
            <div className="rounded-xl border border-dashed border-[var(--tech-accent)]/50 bg-[var(--tech-accent)]/5 p-3">
              <div className="flex items-center gap-2 mb-1.5">
                <Upload size={14} className="text-[var(--tech-accent)]" />
                <span className="text-sm font-semibold">Import from OneNote / docs</span>
              </div>
              <p className="text-xs text-[var(--text-muted)] mb-2.5">Upload a PDF, Word (.docx) or text export of your notebook — Apex splits it into clean fixes automatically.</p>
              <input ref={fileRef} type="file" accept=".pdf,.docx,.txt,.md,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                onChange={onIngest} className="hidden" data-testid="kb-ingest-input" />
              <button data-testid="kb-ingest-btn" disabled={ingesting} onClick={() => fileRef.current?.click()}
                className="w-full flex items-center justify-center gap-1.5 rounded-lg border border-[var(--tech-accent)] text-[var(--tech-accent)] font-semibold py-2 text-sm disabled:opacity-50 hover:bg-[var(--tech-accent)]/10 transition-all">
                {ingesting ? <><Loader2 size={15} className="animate-spin" /> Importing…</> : <><Upload size={15} /> Upload document</>}
              </button>
            </div>
            <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-3 space-y-2">
              <input data-testid="kb-title" value={kbForm.title} onChange={(e) => setKbForm({ ...kbForm, title: e.target.value })}
                placeholder="Problem / title (e.g. Outlook won't sync)"
                className="w-full rounded-lg bg-black/30 border border-[var(--border)] px-3 py-2 text-sm outline-none focus:border-[var(--tech-accent)]" />
              <textarea data-testid="kb-resolution" value={kbForm.resolution} onChange={(e) => setKbForm({ ...kbForm, resolution: e.target.value })}
                placeholder="Resolution / steps that fixed it" rows={3}
                className="w-full rounded-lg bg-black/30 border border-[var(--border)] px-3 py-2 text-sm outline-none focus:border-[var(--tech-accent)] resize-none" />
              <button data-testid="kb-save" onClick={saveKb}
                className="w-full flex items-center justify-center gap-1.5 rounded-lg bg-[var(--tech-accent)] text-black font-semibold py-2 text-sm hover:brightness-110 transition-all">
                <Plus size={15} /> Save fix
              </button>
            </div>
            {kb.length === 0 && <p className="text-xs text-[var(--text-muted)] px-1">No fixes yet. As Apex generates diagnostics and you save fixes, they build up here and Apex reuses them.</p>}
            {kb.map((k) => (
              <div key={k.id} className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-3" data-testid="kb-item">
                <div className="flex items-center gap-2 mb-1">
                  <Wrench size={13} className="text-[var(--tech-accent)]" />
                  <span className="text-sm font-semibold flex-1">{k.title}</span>
                  <span className="text-[10px] font-mono-tech text-[var(--text-muted)] uppercase">{k.source}</span>
                </div>
                {k.resolution && <p className="text-sm text-[var(--text-secondary)]">{k.resolution}</p>}
              </div>
            ))}
          </div>
        ) : tab === "ckb" ? (
          <div className="p-4 space-y-3">
            <div className="rounded-xl border border-dashed border-[var(--primary)]/50 bg-[var(--primary)]/5 p-3">
              <div className="flex items-center gap-2 mb-1.5">
                <LifeBuoy size={14} className="text-[var(--primary)]" />
                <span className="text-sm font-semibold">Customer self-help guides</span>
              </div>
              <p className="text-xs text-[var(--text-muted)] mb-2.5">Plain-English how-tos customers can browse (Help button) and that Apex uses to answer them. Upload a doc to auto-create guides.</p>
              <input ref={ckbFileRef} type="file" accept=".pdf,.docx,.txt,.md,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                onChange={onCkbIngest} className="hidden" data-testid="ckb-ingest-input" />
              <button data-testid="ckb-ingest-btn" disabled={ckbIngesting} onClick={() => ckbFileRef.current?.click()}
                className="w-full flex items-center justify-center gap-1.5 rounded-lg border border-[var(--primary)] text-[var(--primary)] font-semibold py-2 text-sm disabled:opacity-50 hover:bg-[var(--primary)]/10 transition-all">
                {ckbIngesting ? <><Loader2 size={15} className="animate-spin" /> Importing…</> : <><Upload size={15} /> Upload document</>}
              </button>
            </div>
            <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-3 space-y-2">
              <input data-testid="ckb-title" value={ckbForm.title} onChange={(e) => setCkbForm({ ...ckbForm, title: e.target.value })}
                placeholder="Question / topic (e.g. How do I reset my password?)"
                className="w-full rounded-lg bg-black/30 border border-[var(--border)] px-3 py-2 text-sm outline-none focus:border-[var(--primary)]" />
              <input data-testid="ckb-category" value={ckbForm.category} onChange={(e) => setCkbForm({ ...ckbForm, category: e.target.value })}
                placeholder="Category (optional, e.g. Email)"
                className="w-full rounded-lg bg-black/30 border border-[var(--border)] px-3 py-2 text-sm outline-none focus:border-[var(--primary)]" />
              <textarea data-testid="ckb-resolution" value={ckbForm.resolution} onChange={(e) => setCkbForm({ ...ckbForm, resolution: e.target.value })}
                placeholder="Friendly step-by-step for the customer" rows={3}
                className="w-full rounded-lg bg-black/30 border border-[var(--border)] px-3 py-2 text-sm outline-none focus:border-[var(--primary)] resize-none" />
              <button data-testid="ckb-save" onClick={saveCkb}
                className="w-full flex items-center justify-center gap-1.5 rounded-lg bg-[var(--primary)] text-black font-semibold py-2 text-sm hover:brightness-110 transition-all">
                <Plus size={15} /> Save guide
              </button>
            </div>
            {ckb.length === 0 && <p className="text-xs text-[var(--text-muted)] px-1">No customer guides yet. Add one above or upload a document.</p>}
            {ckb.map((c) => (
              <div key={c.id} className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-3" data-testid="ckb-item">
                <div className="flex items-center gap-2 mb-1">
                  <LifeBuoy size={13} className="text-[var(--primary)]" />
                  <span className="text-sm font-semibold flex-1">{c.title}</span>
                  {c.category && <span className="text-[10px] font-mono-tech text-[var(--text-muted)] uppercase">{c.category}</span>}
                  <button data-testid="ckb-delete" onClick={() => removeCkb(c.id)}
                    className="h-6 w-6 grid place-items-center rounded text-[var(--text-muted)] hover:text-[var(--alert)] transition-colors">
                    <Trash2 size={13} />
                  </button>
                </div>
                {c.resolution && <p className="text-sm text-[var(--text-secondary)] whitespace-pre-line">{c.resolution}</p>}
              </div>
            ))}
          </div>
        ) : (
          <div className="p-4 space-y-3">
            <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-3 space-y-2">
              <input data-testid="f-customer" value={fForm.customer} onChange={(e) => setFForm({ ...fForm, customer: e.target.value })}
                placeholder="Customer name" className="w-full rounded-lg bg-black/30 border border-[var(--border)] px-3 py-2 text-sm outline-none focus:border-[var(--tech-accent)]" />
              <input data-testid="f-contact" value={fForm.contact} onChange={(e) => setFForm({ ...fForm, contact: e.target.value })}
                placeholder="Phone / email" className="w-full rounded-lg bg-black/30 border border-[var(--border)] px-3 py-2 text-sm outline-none focus:border-[var(--tech-accent)]" />
              <textarea data-testid="f-note" value={fForm.note} onChange={(e) => setFForm({ ...fForm, note: e.target.value })}
                placeholder="What to follow up on" rows={2}
                className="w-full rounded-lg bg-black/30 border border-[var(--border)] px-3 py-2 text-sm outline-none focus:border-[var(--tech-accent)] resize-none" />
              <button data-testid="f-save" onClick={saveF}
                className="w-full flex items-center justify-center gap-1.5 rounded-lg bg-[var(--tech-accent)] text-black font-semibold py-2 text-sm hover:brightness-110 transition-all">
                <Plus size={15} /> Add follow-up
              </button>
            </div>
            {fups.length === 0 && <p className="text-xs text-[var(--text-muted)] px-1">No follow-ups. (Outbound follow-up calls activate once the phone line is connected.)</p>}
            {fups.map((f) => (
              <div key={f.id} className={`rounded-xl border border-[var(--border)] bg-[var(--surface)] p-3 flex items-start gap-2 ${f.done ? "opacity-50" : ""}`} data-testid="followup-item">
                <button data-testid="followup-toggle" onClick={() => toggle(f.id)}
                  className={`mt-0.5 h-5 w-5 rounded grid place-items-center border ${f.done ? "bg-[var(--tech-accent)] border-[var(--tech-accent)]" : "border-[var(--border)]"}`}>
                  {f.done && <Check size={13} className="text-black" />}
                </button>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${f.done ? "line-through text-[var(--text-muted)]" : "text-[var(--text-primary)]"}`}>{f.note}</p>
                  {(f.customer || f.contact) && <p className="text-xs text-[var(--text-muted)] mt-0.5">{[f.customer, f.contact].filter(Boolean).join(" · ")}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}

function BillsView() {
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { getBills().then((d) => setBills(d.bills || [])).catch(() => {}).finally(() => setLoading(false)); }, []);
  return (
    <div className="flex flex-col h-full" data-testid="bills-view">
      <div className="px-4 py-3 border-b border-[var(--border)] flex items-center gap-2">
        <Receipt size={15} className="text-[var(--secondary-accent)]" />
        <span className="font-display font-semibold text-sm flex-1">Outstanding Bills</span>
        <span className="text-xs font-mono-tech text-[var(--text-muted)]">{bills.length}</span>
      </div>
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-2">
          {loading && <p className="text-sm text-[var(--text-muted)]">Loading…</p>}
          {!loading && bills.length === 0 && <p className="text-xs text-[var(--text-muted)]">No outstanding bills. Assign it IN A bit jobs to the "apex" user to have them show here.</p>}
          {bills.map((b) => (
            <div key={b.id} className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-3" data-testid="bill-item">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-semibold flex-1">{b.customer_name || "—"}</span>
                <span className="text-[10px] font-mono-tech px-2 py-0.5 rounded bg-black/30 border border-[var(--border)] text-[var(--secondary-accent)]">{b.status}</span>
              </div>
              <p className="text-xs text-[var(--text-secondary)]">{b.description || b.job_type}</p>
              <p className="text-[11px] text-[var(--text-muted)] mt-1 font-mono-tech">{b.appointment_date} · {b.estimated_hours}h · {b.location}</p>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}

export function StaffPanel({ open, jobsSearch, onClose, onStaffChange, onStaffLogin }) {
  const [staff, setStaffState] = useState(null);
  const setStaff = useCallback((s) => { setStaffState(s); onStaffChange && onStaffChange(s); }, [onStaffChange]);
  const handleLogin = useCallback((res) => {
    setStaffState(res);
    onStaffChange && onStaffChange(res);
    onStaffLogin && onStaffLogin(res);
  }, [onStaffChange, onStaffLogin]);

  useEffect(() => {
    if (open && getStaffToken()) {
      staffMe().then(setStaff).catch(() => { setStaffToken(""); setStaff(null); });
    }
  }, [open, setStaff]);

  const logout = () => { setStaffToken(""); setStaff(null); toast.info("Signed out"); };

  const body = (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 px-4 h-14 border-b border-[var(--border)] shrink-0">
        <Lock size={15} className="text-[var(--primary)]" />
        <span className="font-display font-semibold text-sm flex-1">
          {staff ? (staff.is_tech ? "Tech Resource" : staff.is_accounts ? "Accounts" : "Staff") : "Staff Area"}
        </span>
        {staff && (
          <button data-testid="staff-logout" onClick={logout} title="Sign out" className="h-8 w-8 grid place-items-center rounded-lg text-[var(--text-muted)] hover:text-[var(--alert)] hover:bg-white/5 transition-colors">
            <LogOut size={16} />
          </button>
        )}
        <button data-testid="staff-panel-close" onClick={onClose} className="h-8 w-8 grid place-items-center rounded-lg text-[var(--text-muted)] hover:text-white hover:bg-white/5 transition-colors"><X size={18} /></button>
      </div>
      <div className="flex-1 min-h-0">
        {!staff ? <LoginForm onLoggedIn={handleLogin} />
          : staff.is_tech ? <TechResource jobsSearch={jobsSearch} />
          : staff.is_accounts ? <BillsView />
          : <div className="p-6 text-sm text-[var(--text-secondary)]">Signed in as <b>{staff.email}</b>. This account has no private staff area. Tech Resource is for the technician account; Bills is for the accounts team.</div>}
      </div>
    </div>
  );

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/60" onClick={onClose} data-testid="staff-panel">
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="absolute right-0 top-0 h-full w-[26rem] max-w-[90vw] bg-[var(--surface-2)] glass border-l border-[var(--border)]"
            onClick={(e) => e.stopPropagation()}>
            {body}
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
